import { Controller } from "@hotwired/stimulus"
import { computePosition, offset, flip, shift, autoUpdate } from "@floating-ui/dom"

export default class extends Controller {
  static targets = ["content"]

  static values = {
    placement: { type: String, default: "top" },
    trigger: { type: String, default: "click" },
    showDelay: { type: Number, default: 150 },
    hideDelay: { type: Number, default: 200 }
  }

  connect() {
    this.cleanupAutoUpdate = null
    this.outsideClickHandler = null
    this.escapeHandler = null
    this.currentPopoverElement = null
    this.activeTrigger = null
    this.userOnClose = null
    this.showTimeout = null
    this.hideTimeout = null

    if (this.triggerValue === "hover") {
      this.boundScheduleShow = this.scheduleShow.bind(this)
      this.boundScheduleHide = this.scheduleHide.bind(this)
      this.boundCancelHide = this.cancelHide.bind(this)

      this.element.addEventListener("mouseenter", this.boundScheduleShow)
      this.element.addEventListener("mouseleave", this.boundScheduleHide)
      this.element.addEventListener("focusin", this.boundScheduleShow)
      this.element.addEventListener("focusout", this.boundScheduleHide)
    }
  }

  disconnect() {
    this.#clearTimers()
    this.#removeHoverListeners()
    this.close()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    const trigger = event.currentTarget

    if (this.activeTrigger === trigger && this.currentPopoverElement) {
      this.close()
      return
    }

    this.#openFromTemplate(trigger)
  }

  scheduleShow() {
    this.#clearTimers()
    this.showTimeout = setTimeout(() => this.#openFromTemplate(), this.showDelayValue)
  }

  scheduleHide() {
    this.#clearTimers()
    this.hideTimeout = setTimeout(() => this.close(), this.hideDelayValue)
  }

  cancelHide() {
    clearTimeout(this.hideTimeout)
    this.hideTimeout = null
  }

  async #openFromTemplate(triggerElement = null) {
    if (!this.hasContentTarget) return

    const trigger = triggerElement || this.element.querySelector("button") || this.element
    const html = this.contentTarget.innerHTML

    const popover = await this.showFor(trigger, {
      html,
      placement: this.placementValue,
      interaction: this.triggerValue
    })

    if (popover && this.triggerValue === "hover") {
      popover.addEventListener("mouseenter", this.boundCancelHide)
      popover.addEventListener("mouseleave", this.boundScheduleHide)
    }
  }

  async showFor(triggerElement, { html, placement = "right-start", popoverClass = "", onClose = null, interaction = "click" } = {}) {
    if (!triggerElement || !html) return null

    this.close()
    this.userOnClose = onClose

    this.currentPopoverElement = document.createElement("div")
    this.currentPopoverElement.className =
      "fixed z-[80] min-w-[280px] max-w-[340px] rounded-xl border border-neutral-200 bg-white text-neutral-900 shadow-2xl pointer-events-auto"

    if (popoverClass) {
      this.currentPopoverElement.className = `${this.currentPopoverElement.className} ${popoverClass}`
    }

    this.currentPopoverElement.innerHTML = html
    document.body.appendChild(this.currentPopoverElement)
    this.#bindCopyButtons()

    this.activeTrigger = triggerElement

    await this.#updatePosition(triggerElement, placement)

    this.cleanupAutoUpdate = autoUpdate(triggerElement, this.currentPopoverElement, () => {
      this.#updatePosition(triggerElement, placement)
    })

    if (interaction === "hover") {
      this.escapeHandler = (event) => {
        if (event.key === "Escape") this.close()
      }
      document.addEventListener("keydown", this.escapeHandler)
    } else {
      this.outsideClickHandler = (event) => {
        if (!this.currentPopoverElement) return
        if (this.currentPopoverElement.contains(event.target)) return
        if (triggerElement.contains(event.target)) return
        this.close()
      }

      this.escapeHandler = (event) => {
        if (event.key === "Escape") this.close()
      }

      document.addEventListener("click", this.outsideClickHandler, true)
      document.addEventListener("keydown", this.escapeHandler)
    }

    return this.currentPopoverElement
  }

  #bindCopyButtons() {
    if (!this.currentPopoverElement) return

    this.boundCopyHandler = this.boundCopyHandler || this.#handleCopyClick.bind(this)
    this.currentPopoverElement.addEventListener("click", this.boundCopyHandler)
  }

  #handleCopyClick(event) {
    const button = event.target.closest("[data-copy-text]")
    if (!button) return

    event.preventDefault()
    event.stopPropagation()

    const text = button.dataset.copyText
    if (!text) return

    navigator.clipboard.writeText(text).then(() => {
      const originalTitle = button.getAttribute("title")
      button.setAttribute("title", "¡Copiado!")
      setTimeout(() => {
        if (originalTitle) {
          button.setAttribute("title", originalTitle)
        } else {
          button.removeAttribute("title")
        }
      }, 1500)
    })
  }

  close() {
    this.#clearTimers()

    if (this.cleanupAutoUpdate) {
      this.cleanupAutoUpdate()
      this.cleanupAutoUpdate = null
    }

    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler, true)
      this.outsideClickHandler = null
    }

    if (this.escapeHandler) {
      document.removeEventListener("keydown", this.escapeHandler)
      this.escapeHandler = null
    }

    if (this.currentPopoverElement) {
      if (this.boundCopyHandler) {
        this.currentPopoverElement.removeEventListener("click", this.boundCopyHandler)
      }
      if (this.boundCancelHide) {
        this.currentPopoverElement.removeEventListener("mouseenter", this.boundCancelHide)
        this.currentPopoverElement.removeEventListener("mouseleave", this.boundScheduleHide)
      }
      this.currentPopoverElement.remove()
      this.currentPopoverElement = null
    }

    this.activeTrigger = null

    if (typeof this.userOnClose === "function") {
      this.userOnClose()
    }
    this.userOnClose = null
  }

  #clearTimers() {
    clearTimeout(this.showTimeout)
    clearTimeout(this.hideTimeout)
    this.showTimeout = null
    this.hideTimeout = null
  }

  #removeHoverListeners() {
    if (this.triggerValue !== "hover") return

    this.element.removeEventListener("mouseenter", this.boundScheduleShow)
    this.element.removeEventListener("mouseleave", this.boundScheduleHide)
    this.element.removeEventListener("focusin", this.boundScheduleShow)
    this.element.removeEventListener("focusout", this.boundScheduleHide)
  }

  async #updatePosition(triggerElement, placement) {
    if (!this.currentPopoverElement) return

    const { x, y } = await computePosition(triggerElement, this.currentPopoverElement, {
      placement,
      middleware: [offset(10), flip(), shift({ padding: 8 })]
    })

    Object.assign(this.currentPopoverElement.style, {
      left: `${x}px`,
      top: `${y}px`
    })
  }
}
